import { useEffect, useRef } from 'react';
import type { Course, MapStyle } from '@thrasher/shared';
import type { Game } from '../game/Game.js';

const SIZE = 190;
const RANGE = 95;
const SCALE = 2;

const STYLE: Record<MapStyle, { fill: string; stroke: string; width?: number }> = {
  pool: { fill: 'rgba(90,190,255,0.55)', stroke: 'rgba(200,240,255,0.9)' },
  ramp: { fill: 'rgba(255,190,90,0.45)', stroke: 'rgba(255,220,160,0.8)' },
  block: { fill: 'rgba(210,210,220,0.35)', stroke: 'rgba(240,240,250,0.6)' },
  rail: { fill: 'transparent', stroke: 'rgba(255,90,80,0.95)', width: 2 },
  dirt: { fill: 'rgba(190,130,80,0.55)', stroke: 'rgba(230,170,110,0.8)' },
  grass: { fill: 'rgba(80,160,80,0.4)', stroke: 'transparent' },
  wall: { fill: 'rgba(120,120,140,0.5)', stroke: 'transparent' },
  plaza: { fill: 'rgba(220,200,170,0.25)', stroke: 'transparent' },
};

function renderBase(course: Course): HTMLCanvasElement {
  const half = course.halfExtent;
  const canvas = document.createElement('canvas');
  canvas.width = half * 2 * SCALE;
  canvas.height = half * 2 * SCALE;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(16,18,26,0.82)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const map = (x: number, z: number): [number, number] => [(x + half) * SCALE, (z + half) * SCALE];
  ctx.fillStyle = 'rgba(210,190,160,0.12)';
  const [px, pz] = map(-50, -45);
  ctx.fillRect(px, pz, 100 * SCALE, 90 * SCALE);
  for (const shape of course.shapes) {
    const style = STYLE[shape.style];
    ctx.beginPath();
    shape.points.forEach(([x, z], index) => {
      const [cx, cz] = map(x, z);
      if (index === 0) ctx.moveTo(cx, cz);
      else ctx.lineTo(cx, cz);
    });
    if (shape.closed) {
      ctx.closePath();
      ctx.fillStyle = style.fill;
      ctx.fill();
    }
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = style.width ?? 1;
    ctx.stroke();
  }
  ctx.font = '700 15px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  for (const zone of course.zones) {
    const [zx, zz] = map(zone.x, zone.z);
    ctx.fillText(zone.name.toUpperCase(), zx, zz);
  }
  return canvas;
}

/** Rotating radar-style minimap centred on the local rider. */
export function Minimap({ game }: { game: Game }): React.JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d')!;
    const base = renderBase(game.course);
    const half = game.course.halfExtent;
    let frame = 0;
    let last = 0;
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      if (now - last < 50) return;
      last = now;
      const markers = game.markers();
      const self = markers.find((m) => m.self);
      const cx = self?.x ?? 0;
      const cz = self?.z ?? 0;
      const yaw = game.cameraYaw();
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.save();
      ctx.beginPath();
      ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.translate(SIZE / 2, SIZE / 2);
      // Camera forward points up on the map.
      ctx.rotate(Math.PI + yaw);
      const scale = SIZE / 2 / RANGE;
      ctx.scale(scale / SCALE, scale / SCALE);
      ctx.drawImage(base, -(cx + half) * SCALE, -(cz + half) * SCALE);
      ctx.restore();
      ctx.save();
      ctx.translate(SIZE / 2, SIZE / 2);
      ctx.rotate(Math.PI + yaw);
      for (const marker of markers) {
        const dx = (marker.x - cx) * scale;
        const dz = (marker.z - cz) * scale;
        const distance = Math.hypot(dx, dz);
        const limit = SIZE / 2 - 8;
        const k = distance > limit ? limit / distance : 1;
        ctx.save();
        ctx.translate(dx * k, dz * k);
        ctx.rotate(-marker.heading + Math.PI);
        ctx.fillStyle = marker.color;
        ctx.strokeStyle = marker.self ? '#ffffff' : 'rgba(0,0,0,0.7)';
        ctx.lineWidth = marker.self ? 2 : 1.5;
        ctx.beginPath();
        const s = marker.self ? 7 : 5.5;
        ctx.moveTo(0, -s);
        ctx.lineTo(s * 0.75, s * 0.8);
        ctx.lineTo(0, s * 0.35);
        ctx.lineTo(-s * 0.75, s * 0.8);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2, 0, Math.PI * 2);
      ctx.stroke();
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [game]);
  return <canvas ref={ref} className="minimap" width={SIZE} height={SIZE} aria-label="Park map" />;
}
